"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { Wrapper } from "@googlemaps/react-wrapper";
import api from "@/lib/api";
import { ApiRoutes } from "@/lib/routeResolver";
import { isValidEmail, sanitizePhone, isValidPhone } from "@/lib/validators";
import {
  submitWebsiteGuestLead,
  type DestinationInRegion,
  type DestinationWithPackage,
  type TravelNetwork,
  type HostAgency,
  type MiniPackage,
} from "@/lib/requestQuote";
import type { UtmCodes } from "@/lib/utm/params";
import SearchableSelect from "./SearchableSelect";
import styles from "./request-quote.module.css";

/* ── Helpers ────────────────────────────────────────────────────── */

const COMPANY_ID = process.env.NEXT_PUBLIC_COMPANY_ID!;
const GOOGLE_KEY = process.env.NEXT_PUBLIC_GOOGLE_API_KEY!;
const IATA_RE = /^[A-Za-z0-9]{7,8}$/;

const CRM_TYPES = [
  "Travel Agency",
  "Tour Operator",
  "Association",
  "Chamber",
  "Consultant",
] as const;

function getCookieValue(name: string): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : "";
}

function convertInputDateToShortDate(d: string): string {
  if (!d) return "";
  const parts = d.split("-");
  if (parts.length === 3) return `${parts[1]}/${parts[2]}/${parts[0]}`;
  return d;
}

/* ── Address decoder ────────────────────────────────────────────── */

function decodeAddressComponent(
  components: google.maps.GeocoderAddressComponent[],
) {
  let streetNumber = "";
  let streetName = "";
  let city = "";
  let state = "";
  let zip = "";
  let country = "";

  for (const c of components) {
    const types = c.types;
    if (types.includes("street_number")) streetNumber = c.long_name;
    else if (types.includes("route")) streetName = c.long_name;
    else if (types.includes("locality")) city = c.long_name;
    else if (types.includes("administrative_area_level_1"))
      state = c.short_name;
    else if (types.includes("postal_code")) zip = c.long_name;
    else if (types.includes("country")) country = c.long_name;
  }

  return {
    full_Address:
      `${streetNumber} ${streetName}, ${city}, ${state} ${zip}, ${country}`.trim(),
    streetNumber,
    streetName,
    city,
    state,
    zip,
    country,
  };
}

/* ── Interfaces ─────────────────────────────────────────────────── */

interface LocationInfo {
  addressTitle: string;
  phone1: string;
  tnAddress: {
    full_Address: string;
    apt_floor: string;
    streetNumber: string;
    streetName: string;
    city: string;
    state: string;
    zip: string;
    country: string;
    county: string;
    lat: number;
    lng: number;
  };
  contactPersonsList: Array<{
    contactPersonTitle: string;
    firstName: string;
    lastName: string;
    businessPhone: string;
    email: string;
    utmCodes?: Record<string, string>;
  }>;
}

interface DestinationEntry {
  packageId: string | null;
  destination_Id: string | null;
  numberOfNights: number;
  packages: MiniPackage[];
  duplicatePackage: boolean;
}

interface FieldExist {
  clia: boolean;
  true: boolean;
  vtc: boolean;
  arc: boolean;
  iata: boolean;
}

interface ClientQuoteFormProps {
  email: string;
  clientExist: boolean;
  guestId: string | null;
  data: Record<string, unknown> | null;
  utm: UtmCodes;
  referrer: string;
  destinationsInRegions: DestinationInRegion[];
  destinationsWithPackages: DestinationWithPackage[];
  onStepChange: (step: number) => void;
  onValidChange?: (valid: boolean) => void;
}

/* ── Places Autocomplete Sub-Component ──────────────────────────── */

function PlacesInput({
  value,
  onChange,
  onSelect,
}: {
  value: string;
  onChange: (v: string) => void;
  onSelect: (
    place: google.maps.places.PlaceResult,
    coords: { lat: number; lng: number },
  ) => void;
}) {
  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [open, setOpen] = useState(false);
  const serviceRef = useRef<google.maps.places.AutocompleteService | null>(
    null,
  );
  const placesRef = useRef<google.maps.places.PlacesService | null>(null);
  const dummyRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (typeof google !== "undefined" && google.maps?.places) {
      serviceRef.current = new google.maps.places.AutocompleteService();
      if (dummyRef.current) {
        placesRef.current = new google.maps.places.PlacesService(
          dummyRef.current,
        );
      }
    }
  }, []);

  const handleInput = (v: string) => {
    onChange(v);
    if (v.length > 2 && serviceRef.current) {
      serviceRef.current.getPlacePredictions(
        { input: v, types: ["geocode"] },
        (results) => {
          setSuggestions(results ?? []);
          setOpen(true);
        },
      );
    } else {
      setSuggestions([]);
      setOpen(false);
    }
  };

  const handleSelect = (
    prediction: google.maps.places.AutocompletePrediction,
  ) => {
    setOpen(false);
    onChange(prediction.description);
    if (placesRef.current) {
      placesRef.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: ["address_components", "formatted_address", "geometry"],
        },
        (place) => {
          if (place) {
            const lat = place.geometry?.location?.lat() ?? 0;
            const lng = place.geometry?.location?.lng() ?? 0;
            onSelect(place, { lat, lng });
          }
        },
      );
    }
  };

  return (
    <div className={styles.placesWrap}>
      <i className={`fa-solid fa-location-dot ${styles.placesIcon}`} />
      <input
        type="text"
        placeholder="Search address..."
        value={value}
        onChange={(e) => handleInput(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
      />
      {open && suggestions.length > 0 && (
        <ul className={styles.placesSuggestions}>
          {suggestions.map((s) => (
            <li
              key={s.place_id}
              className={styles.placesSuggestionItem}
              onMouseDown={() => handleSelect(s)}
            >
              <i className="fa-solid fa-location-dot" />
              {s.description}
            </li>
          ))}
        </ul>
      )}
      <div ref={dummyRef} style={{ display: "none" }} />
    </div>
  );
}

/* ── Main Form Component ────────────────────────────────────────── */

export default function ClientQuoteForm({
  email,
  clientExist,
  guestId,
  data,
  utm,
  referrer,
  destinationsInRegions,
  destinationsWithPackages,
  onStepChange,
  onValidChange,
}: ClientQuoteFormProps) {
  const d = data as Record<string, unknown> | null;
  const dLoc = (d?.locationInformation as LocationInfo[] | undefined) ?? [];
  const dContact = dLoc[0]?.contactPersonsList?.[0];

  // Dropdowns
  const [travelNetworks, setTravelNetworks] = useState<TravelNetwork[]>([]);
  const [hostAgencies, setHostAgencies] = useState<HostAgency[]>([]);

  // Form fields
  const [type, setType] = useState<string>(
    (d?.type as string) ?? "Travel Agency",
  );
  const [name, setName] = useState<string>((d?.name as string) ?? "");
  const [iata, setIata] = useState<string>((d?.iata as string) ?? "");
  const [clia, setClia] = useState<string>((d?.clia as string) ?? "");
  const [arc, setArc] = useState<string>((d?.arc as string) ?? "");
  const [trueField, setTrueField] = useState<string>((d?.true as string) ?? "");
  const [vtc, setVtc] = useState<string>((d?.vtc as string) ?? "");
  const [travelNetworkId, setTravelNetworkId] = useState<string>(
    (d?.travelNetworkId as string) ?? "",
  );
  const [hostAgencyId, setHostAgencyId] = useState<string>(
    (d?.hostAgencyId as string) ?? "",
  );

  // Location
  const [addressSearch, setAddressSearch] = useState<string>(
    dLoc[0]?.tnAddress?.full_Address ?? "",
  );
  const [locationInfo, setLocationInfo] = useState<LocationInfo>(
    dLoc[0] ?? {
      addressTitle: "",
      phone1: "",
      tnAddress: {
        full_Address: "",
        apt_floor: "",
        streetNumber: "",
        streetName: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        county: "",
        lat: 0,
        lng: 0,
      },
      contactPersonsList: [
        {
          contactPersonTitle: "",
          firstName: "",
          lastName: "",
          businessPhone: "",
          email: email,
          utmCodes: {
            url: utm.url ?? "",
            utm_source: utm.utm_source ?? "",
            utm_campaign: utm.utm_campaign ?? "",
            utm_medium: utm.utm_medium ?? "",
            utm_content: utm.utm_content ?? "",
            utm_term: utm.utm_term ?? "",
          },
        },
      ],
    },
  );

  // Lead fields
  const [leadName, setLeadName] = useState("");
  const [numberOfTravellers, setNumberOfTravellers] = useState(0);
  const [budgetType, setBudgetType] = useState<"byPerson" | "byTrip">(
    "byPerson",
  );
  const [budget, setBudget] = useState(0);
  const [preferredDate, setPreferredDate] = useState("");
  const [netOrGrowth, setNetOrGrowth] = useState<"Net" | "Growth">("Net");
  const [notes, setNotes] = useState("");
  const [byPackage, setByPackage] = useState(false);

  // Destinations
  const [destinations, setDestinations] = useState<DestinationEntry[]>([
    {
      packageId: null,
      destination_Id: null,
      numberOfNights: 0,
      packages: [],
      duplicatePackage: false,
    },
  ]);

  // Validation / errors
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [fieldExist, setFieldExist] = useState<FieldExist>({
    clia: false,
    true: false,
    vtc: false,
    arc: false,
    iata: false,
  });
  const [checkError, setCheckError] = useState(false);

  const touch = (field: string) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  // Fetch networks & agencies on mount
  useEffect(() => {
    api
      .get(ApiRoutes.ADMIN_TRAVELNETWORK, {
        params: { CompanyId: COMPANY_ID, PageSize: 100, PageNumber: 1 },
      })
      .then((res) => setTravelNetworks(res.data?.data ?? []))
      .catch(() => {});

    api
      .get(ApiRoutes.ADMIN_HOSTAGENCY, {
        params: { CompanyId: COMPANY_ID, PageSize: 100, PageNumber: 1 },
      })
      .then((res) => setHostAgencies(res.data?.data ?? []))
      .catch(() => {});
  }, []);

  // Update contact person helper
  const updateContact = (field: string, value: string) => {
    setLocationInfo((prev) => {
      const cp = { ...prev.contactPersonsList[0], [field]: value };
      return {
        ...prev,
        contactPersonsList: [cp],
      };
    });
  };

  // Address selection
  const handlePlaceSelect = (
    place: google.maps.places.PlaceResult,
    coords: { lat: number; lng: number },
  ) => {
    if (!place.address_components) return;
    const decoded = decodeAddressComponent(place.address_components);
    setLocationInfo((prev) => ({
      ...prev,
      tnAddress: {
        ...prev.tnAddress,
        full_Address: place.formatted_address ?? "",
        streetNumber: decoded.streetNumber,
        streetName: decoded.streetName,
        city: decoded.city,
        state: decoded.state,
        zip: decoded.zip,
        country: decoded.country,
        lat: coords.lat,
        lng: coords.lng,
      },
    }));
    setAddressSearch(place.formatted_address ?? "");
  };

  // Destination management
  const addDestination = () => {
    setDestinations((prev) => [
      ...prev,
      {
        packageId: null,
        destination_Id: null,
        numberOfNights: 0,
        packages: [],
        duplicatePackage: false,
      },
    ]);
  };

  const removeDestination = (index: number) => {
    setDestinations((prev) => prev.filter((_, i) => i !== index));
  };

  const updateDestination = (
    index: number,
    field: keyof DestinationEntry,
    value: unknown,
  ) => {
    setDestinations((prev) =>
      prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)),
    );
  };

  // Handle destination country change (fetch packages if byPackage)
  const handleDestinationChange = useCallback(
    async (index: number, destId: string | null) => {
      updateDestination(index, "destination_Id", destId);
      updateDestination(index, "packageId", null);
      updateDestination(index, "numberOfNights", 0);

      if (byPackage && destId) {
        const target = destinationsInRegions.find(
          (d) => d.codeIso2Country === destId,
        );
        if (target?.id) {
          try {
            setLoading(true);
            const res = await api.get(
              ApiRoutes.INVENTORY_PACKAGE_MINI_WEBSITE,
              {
                params: {
                  CompanyId: COMPANY_ID,
                  IsWebsite: true,
                  CountryId: target.id,
                  PageSize: 1000,
                  PageNumber: 1,
                },
              },
            );
            updateDestination(index, "packages", res.data?.data ?? []);
          } catch {
            updateDestination(index, "packages", []);
          } finally {
            setLoading(false);
          }
        } else {
          updateDestination(index, "packages", []);
        }
      } else {
        updateDestination(index, "packages", []);
      }
    },
    [byPackage, destinationsInRegions],
  );

  // Handle package selection
  const handlePackageChange = (index: number, pkgId: string | null) => {
    updateDestination(index, "packageId", pkgId);
    const pkg = destinations[index].packages.find((p) => p.id === pkgId);
    updateDestination(index, "numberOfNights", pkg?.nights ?? 0);
  };

  // Toggle byPackage
  const handleByPackageToggle = () => {
    setByPackage((prev) => !prev);
    setDestinations([
      {
        packageId: null,
        destination_Id: null,
        numberOfNights: 0,
        packages: [],
        duplicatePackage: false,
      },
    ]);
  };

  // Validation checks
  const needsIdentifier = type === "Travel Agency" || type === "Tour Operator";
  const hasIdentifier = !!(iata || clia || arc || trueField || vtc);
  const anyFieldExists =
    fieldExist.iata ||
    fieldExist.clia ||
    fieldExist.arc ||
    fieldExist.true ||
    fieldExist.vtc;

  const contact = locationInfo.contactPersonsList[0];

  // Destination validation — every row must have a destination and nights > 0
  const destinationsValid = destinations.every(
    (d) => d.destination_Id && d.numberOfNights > 0,
  );

  const formValid = !!leadName && destinationsValid && !loading;

  useEffect(() => {
    onValidChange?.(formValid);
  }, [formValid, onValidChange]);

  // Date validation
  const isDateValid = () => {
    if (!preferredDate) return true;
    const selected = new Date(preferredDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selected >= today;
  };

  // Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // For new clients, validate identifier fields
    if (!clientExist) {
      if (needsIdentifier && !hasIdentifier) return;

      const isHostAgency = !!hostAgencyId;
      const info = {
        true: trueField || null,
        vtc: vtc || null,
        arc: arc || null,
        clia: clia || null,
        iata: iata || null,
        type,
      };

      try {
        const exist = await api.post(ApiRoutes.CRM_IS_FIELDS_EXIST, info);
        if (!exist?.data) {
          setCheckError(true);
          setFieldExist({
            arc: false,
            clia: false,
            true: false,
            vtc: false,
            iata: false,
          });
          return;
        }

        const anyExists =
          exist.data.iataExist ||
          exist.data.arcExist ||
          exist.data.cliaExist ||
          exist.data.trueExist ||
          exist.data.vtcExist;

        if (anyExists && !isHostAgency) {
          setCheckError(false);
          setFieldExist({
            arc: !!exist.data.arcExist,
            clia: !!exist.data.cliaExist,
            true: !!exist.data.trueExist,
            vtc: !!exist.data.vtcExist,
            iata: !!exist.data.iataExist,
          });
          return;
        }

        if (anyExists && isHostAgency) {
          const iataNameCheck = await api.get(
            ApiRoutes.CRM_IS_IATA_NAME_EXIST,
            {
              params: {
                iata,
                true: trueField || null,
                vtc: vtc || null,
                arc: arc || null,
                clia: clia || null,
                name,
                type,
              },
            },
          );
          if (iataNameCheck?.data) {
            setFieldExist({
              arc: !!arc,
              clia: !!clia,
              true: !!trueField,
              vtc: !!vtc,
              iata: !!iata,
            });
            return;
          }
          setFieldExist({
            arc: false,
            clia: false,
            true: false,
            vtc: false,
            iata: false,
          });
        }
      } catch {
        setCheckError(true);
        return;
      }

      // Build CRM data and create
      const phone1Clean = sanitizePhone(locationInfo.phone1);
      const bPhoneClean = sanitizePhone(contact.businessPhone);

      const crmData = {
        companyId: COMPANY_ID,
        fromWebsite: true,
        sourceId: "Website",
        travelNetworkId: travelNetworkId || null,
        hostAgencyId: hostAgencyId || null,
        type,
        name,
        iata: iata || null,
        clia: clia || null,
        arc: arc || null,
        true: trueField || null,
        vtc: vtc || null,
        locationInformation: [
          {
            ...locationInfo,
            phone1: phone1Clean,
            contactPersonsList: [
              {
                ...contact,
                businessPhone: bPhoneClean,
              },
            ],
          },
        ],
      };

      setLoading(true);
      try {
        const crmRes = await api.post(ApiRoutes.CRM_CLIENT, crmData);
        const clientId = crmRes?.data?.entityId;
        let clientName: string | null = null;
        try {
          const crmDetail = await api.get(ApiRoutes.CRM_CLIENT_BY_ID(clientId));
          clientName = crmDetail?.data?.data?.name ?? null;
        } catch {
          /* proceed without name */
        }
        await createLead(clientId, clientName, [contact]);
      } catch {
        setLoading(false);
        onStepChange(7);
      }
    } else {
      // Existing client
      const clientId = (d?.id as string) ?? null;
      const clientName = (d?.name as string) ?? null;
      const contactsList = dContact ? [dContact] : [];
      await createLead(clientId, clientName, contactsList);
    }
  };

  const createLead = async (
    clientId: string | null,
    clientName: string | null,
    contactsList: Array<Record<string, unknown>>,
  ) => {
    const destsClean = destinations.map(({ packages: _p, ...rest }) => rest);
    const perPerson = budgetType === "byPerson" ? budget : 0;
    const byTrip = budgetType === "byTrip" ? budget : 0;

    const payload = {
      companyId: COMPANY_ID,
      fromWebsite: true,
      clientType: {
        type,
        clientId,
        clientName,
        contactsPerson: contactsList,
        iata: iata || null,
        clia: clia || null,
        arc: arc || null,
        true: trueField || null,
        vtc: vtc || null,
      },
      leadName,
      numberOfTravellers,
      perPerson,
      byTrip,
      destiations: destsClean,
      utmCodes: { ...utm },
      referralUrl: getCookieValue("myCookieReferrer") || referrer || "",
      byPackage,
      netOrGrowth,
      preferredDepatureDate: preferredDate
        ? convertInputDateToShortDate(preferredDate)
        : null,
      notes,
    };

    setLoading(true);
    try {
      if (guestId) await submitWebsiteGuestLead(guestId);
      await api.post(ApiRoutes.SALES_LEAD, payload);
      setLoading(false);
      onStepChange(6);
    } catch {
      setLoading(false);
      onStepChange(7);
    }
  };

  // Destination options list
  const destOptions = byPackage
    ? destinationsWithPackages
    : destinationsInRegions;

  return (
    <Wrapper apiKey={GOOGLE_KEY} libraries={["places"]}>
      <div className={`${styles.card} ${styles.cardWide}`}>
        {/* Existing client banner */}
        {clientExist && dLoc.length > 0 && (
          <div className={styles.clientBanner}>
            This email address <strong>{email}</strong> is already registered
            for <strong>{(d?.name as string) ?? "Unknown"}</strong>
            {dContact && (
              <>
                <br />
                <i className="fa-regular fa-user" /> Contact Person:{" "}
                <strong>
                  {dContact.firstName} {dContact.lastName}
                </strong>
                <br />
                <i className="fa-regular fa-envelope" /> Contact Email:{" "}
                <a
                  href={`mailto:${dContact.email}`}
                  title={`Email ${dContact.email}`}
                >
                  {dContact.email}
                </a>
                {dContact.businessPhone && (
                  <>
                    <br />
                    <i className="fa-solid fa-phone" /> Contact Phone:{" "}
                    <a
                      href={`tel:${dContact.businessPhone}`}
                      title={`Call ${dContact.businessPhone}`}
                    >
                      {dContact.businessPhone}
                    </a>
                  </>
                )}
              </>
            )}
          </div>
        )}

        <form id="quote-form" onSubmit={handleSubmit}>
          {/* ── Registration Type (new clients only) ── */}
          {!clientExist && (
            <>
              <div>
                <h5 className={styles.sectionTitle}>
                  Select Registration Type{" "}
                  <span style={{ color: "#ef4444" }}>*</span>
                </h5>
                <div className={styles.crmTypeGroup} style={{ marginTop: 12 }}>
                  {CRM_TYPES.map((t) => (
                    <label
                      key={t}
                      className={`${styles.crmTypePill} ${type === t ? styles.crmTypePillActive : ""}`}
                    >
                      {t}
                      <input
                        type="radio"
                        name="reg_type"
                        value={t}
                        checked={type === t}
                        onChange={() => setType(t)}
                      />
                    </label>
                  ))}
                </div>
              </div>

              <hr className={styles.divider} />

              {/* ── Business Details ── */}
              <div>
                <h5 className={styles.sectionTitle}>Business Details</h5>
                <p className={styles.sectionSubtitle}>
                  Please fill the required fields
                </p>

                <div className={styles.row}>
                  <div className={styles.colFull}>
                    <label className={styles.formLabel}>
                      Business Name <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      className={`${styles.formInput} ${touched.name && !name ? styles.formInputError : ""}`}
                      placeholder="Enter Business Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onBlur={() => touch("name")}
                    />
                    {touched.name && !name && (
                      <span className={styles.fieldError}>
                        Business name is required
                      </span>
                    )}
                  </div>

                  {needsIdentifier && (
                    <>
                      {!hasIdentifier && (
                        <div className={styles.colFull}>
                          <div
                            className={`${styles.alert} ${styles.alertWarning}`}
                          >
                            <i className="fa-solid fa-exclamation-triangle" />
                            One of the following fields is required (IATA / CLIA
                            / ARC / TRUE / VTC)
                          </div>
                        </div>
                      )}
                      <div
                        className={`${styles.row} ${styles.row3}`}
                        style={{ gridColumn: "1/-1" }}
                      >
                        <div>
                          <label className={styles.formLabel}>IATA</label>
                          <input
                            className={styles.formInput}
                            placeholder="Enter IATA"
                            value={iata}
                            onChange={(e) => setIata(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className={styles.formLabel}>CLIA</label>
                          <input
                            className={styles.formInput}
                            placeholder="Enter CLIA"
                            value={clia}
                            onChange={(e) => setClia(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className={styles.formLabel}>ARC</label>
                          <input
                            className={styles.formInput}
                            placeholder="Enter ARC"
                            value={arc}
                            onChange={(e) => setArc(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className={styles.formLabel}>TRUE</label>
                          <input
                            className={styles.formInput}
                            placeholder="Enter TRUE"
                            value={trueField}
                            onChange={(e) => setTrueField(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className={styles.formLabel}>VTC</label>
                          <input
                            className={styles.formInput}
                            placeholder="Enter VTC"
                            value={vtc}
                            onChange={(e) => setVtc(e.target.value)}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Field exists warnings */}
              {anyFieldExists && (
                <div
                  className={`${styles.alert} ${styles.alertDanger}`}
                  style={{ marginTop: 12 }}
                >
                  <div>
                    <strong>
                      The following identifiers are already registered:
                    </strong>
                    <ul className={styles.fieldExistList}>
                      {fieldExist.iata && (
                        <li>
                          <i className="fa-solid fa-circle-xmark" /> IATA
                        </li>
                      )}
                      {fieldExist.clia && (
                        <li>
                          <i className="fa-solid fa-circle-xmark" /> CLIA
                        </li>
                      )}
                      {fieldExist.arc && (
                        <li>
                          <i className="fa-solid fa-circle-xmark" /> ARC
                        </li>
                      )}
                      {fieldExist.true && (
                        <li>
                          <i className="fa-solid fa-circle-xmark" /> TRUE
                        </li>
                      )}
                      {fieldExist.vtc && (
                        <li>
                          <i className="fa-solid fa-circle-xmark" /> VTC
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              )}

              {checkError && (
                <div
                  className={`${styles.alert} ${styles.alertDanger}`}
                  style={{ marginTop: 12 }}
                >
                  <i className="fa-solid fa-circle-xmark" />
                  Something went wrong while validating. Please try again.
                </div>
              )}

              <hr className={styles.divider} />

              {/* ── Network & Host Agency ── */}
              <div className={`${styles.row} ${styles.row2}`}>
                <div>
                  <label className={styles.formLabel}>
                    Travel Network{" "}
                    <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                      (Optional)
                    </span>
                  </label>
                  <SearchableSelect
                    options={travelNetworks.map((n) => ({
                      value: n.id,
                      label: n.name,
                    }))}
                    value={travelNetworkId}
                    onChange={setTravelNetworkId}
                    placeholder="-- Select Network --"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className={styles.formLabel}>
                    Host Agency{" "}
                    <span style={{ color: "#9ca3af", fontWeight: 400 }}>
                      (Optional)
                    </span>
                  </label>
                  <SearchableSelect
                    options={hostAgencies.map((a) => ({
                      value: a.id,
                      label: a.name,
                    }))}
                    value={hostAgencyId}
                    onChange={setHostAgencyId}
                    placeholder="-- Select Host Agency --"
                    disabled={loading}
                  />
                </div>
              </div>

              <hr className={styles.divider} />

              {/* ── Location / Address ── */}
              <div>
                <h5 className={styles.sectionTitle}>Location</h5>
                <p className={styles.sectionSubtitle}>
                  Enter your business address
                </p>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    Address <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <PlacesInput
                    value={addressSearch}
                    onChange={setAddressSearch}
                    onSelect={handlePlaceSelect}
                  />
                  {touched.address && !locationInfo.tnAddress.full_Address && (
                    <span className={styles.fieldError}>
                      Address is required
                    </span>
                  )}
                </div>

                <div className={`${styles.row} ${styles.row2}`}>
                  <div>
                    <label className={styles.formLabel}>Phone</label>
                    <input
                      className={styles.formInput}
                      type="tel"
                      placeholder="+1555000000"
                      value={locationInfo.phone1}
                      onChange={(e) =>
                        setLocationInfo((prev) => ({
                          ...prev,
                          phone1: sanitizePhone(e.target.value),
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              <hr className={styles.divider} />

              {/* ── Contact Person ── */}
              <div>
                <h5 className={styles.sectionTitle}>Contact Person</h5>
                <p className={styles.sectionSubtitle}>
                  Primary contact details
                </p>

                <div className={`${styles.row} ${styles.row2}`}>
                  <div>
                    <label className={styles.formLabel}>
                      First Name <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      className={`${styles.formInput} ${touched.firstName && !contact.firstName ? styles.formInputError : ""}`}
                      placeholder="First Name"
                      value={contact.firstName}
                      onChange={(e) =>
                        updateContact("firstName", e.target.value)
                      }
                      onBlur={() => touch("firstName")}
                    />
                    {touched.firstName && !contact.firstName && (
                      <span className={styles.fieldError}>
                        First name is required
                      </span>
                    )}
                  </div>
                  <div>
                    <label className={styles.formLabel}>
                      Last Name <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      className={`${styles.formInput} ${touched.lastName && !contact.lastName ? styles.formInputError : ""}`}
                      placeholder="Last Name"
                      value={contact.lastName}
                      onChange={(e) =>
                        updateContact("lastName", e.target.value)
                      }
                      onBlur={() => touch("lastName")}
                    />
                    {touched.lastName && !contact.lastName && (
                      <span className={styles.fieldError}>
                        Last name is required
                      </span>
                    )}
                  </div>
                  <div>
                    <label className={styles.formLabel}>
                      Email <span style={{ color: "#ef4444" }}>*</span>
                    </label>
                    <input
                      className={styles.formInput}
                      type="email"
                      placeholder="Email Address"
                      value={contact.email}
                      onChange={(e) => updateContact("email", e.target.value)}
                      disabled
                    />
                  </div>
                  <div>
                    <label className={styles.formLabel}>Business Phone</label>
                    <input
                      className={styles.formInput}
                      type="tel"
                      placeholder="+1555000000"
                      value={contact.businessPhone}
                      onChange={(e) =>
                        updateContact(
                          "businessPhone",
                          sanitizePhone(e.target.value),
                        )
                      }
                    />
                  </div>
                </div>
              </div>

              <hr className={styles.divider} />
            </>
          )}

          {/* ── Lead Details (always shown) ── */}
          <div>
            <h5 className={styles.sectionTitle}>Lead Details</h5>
            <p className={styles.sectionSubtitle}>
              Tell us about your travel requirements
            </p>

            <div className={`${styles.row} ${styles.row2}`}>
              <div>
                <label className={styles.formLabel}>
                  Lead Name <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  className={`${styles.formInput} ${touched.leadName && !leadName ? styles.formInputError : ""}`}
                  placeholder="Enter Lead Name"
                  value={leadName}
                  onChange={(e) => setLeadName(e.target.value)}
                  onBlur={() => touch("leadName")}
                />
                {touched.leadName && !leadName && (
                  <span className={styles.fieldError}>
                    Lead name is required
                  </span>
                )}
              </div>
              <div>
                <label className={styles.formLabel}>
                  Number of Travellers{" "}
                  <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  className={styles.formInput}
                  type="number"
                  min={0}
                  placeholder="0"
                  value={numberOfTravellers}
                  onChange={(e) =>
                    setNumberOfTravellers(Number(e.target.value))
                  }
                />
              </div>
            </div>

            <div
              className={`${styles.row} ${styles.row2}`}
              style={{ marginTop: 12 }}
            >
              <div>
                <label className={styles.formLabel}>
                  Preferred Departure Date
                </label>
                <input
                  className={`${styles.formInput} ${preferredDate && !isDateValid() ? styles.formInputError : ""}`}
                  type="date"
                  value={preferredDate}
                  onChange={(e) => setPreferredDate(e.target.value)}
                />
                {preferredDate && !isDateValid() && (
                  <span className={styles.fieldError}>
                    Date must be today or in the future
                  </span>
                )}
              </div>
              <div>
                <label className={styles.formLabel}>Type</label>
                <div className={styles.radioGroup} style={{ marginTop: 4 }}>
                  <label
                    className={`${styles.radioLabel} ${netOrGrowth === "Net" ? styles.radioLabelActive : ""}`}
                    style={{ padding: "8px 16px", minWidth: "unset" }}
                  >
                    Net
                    <input
                      type="radio"
                      name="net_growth"
                      value="Net"
                      checked={netOrGrowth === "Net"}
                      onChange={() => setNetOrGrowth("Net")}
                    />
                  </label>
                  <label
                    className={`${styles.radioLabel} ${netOrGrowth === "Growth" ? styles.radioLabelActive : ""}`}
                    style={{ padding: "8px 16px", minWidth: "unset" }}
                  >
                    Gross
                    <input
                      type="radio"
                      name="net_growth"
                      value="Growth"
                      checked={netOrGrowth === "Growth"}
                      onChange={() => setNetOrGrowth("Growth")}
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>

          <hr className={styles.divider} />

          {/* ── Budget ── */}
          <div>
            <h5 className={styles.sectionTitle}>Budget</h5>
            <div className={styles.budgetTabs}>
              <button
                type="button"
                className={`${styles.budgetTab} ${budgetType === "byPerson" ? styles.budgetTabActive : ""}`}
                onClick={() => setBudgetType("byPerson")}
              >
                Per Person
              </button>
              <button
                type="button"
                className={`${styles.budgetTab} ${budgetType === "byTrip" ? styles.budgetTabActive : ""}`}
                onClick={() => setBudgetType("byTrip")}
              >
                By Trip
              </button>
            </div>
            <input
              className={styles.formInput}
              type="number"
              min={0}
              placeholder={`Budget ${budgetType === "byPerson" ? "per person" : "for trip"}`}
              value={budget}
              onChange={(e) => setBudget(Number(e.target.value))}
              style={{ maxWidth: 300 }}
            />
          </div>

          <hr className={styles.divider} />

          {/* ── Destinations ── */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <h5 className={styles.sectionTitle} style={{ margin: 0 }}>
                Destinations
              </h5>
              <div className={styles.toggleWrap} style={{ margin: 0 }}>
                <button
                  type="button"
                  className={`${styles.toggle} ${byPackage ? styles.toggleActive : ""}`}
                  onClick={handleByPackageToggle}
                />
                <span className={styles.toggleLabel}>By Package</span>
              </div>
            </div>

            {destinations.map((dest, idx) => (
              <div
                key={idx}
                className={`${styles.destRow} ${byPackage ? styles.destRow3 : ""}`}
              >
                <div>
                  <label className={styles.formLabel}>
                    Destination <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <SearchableSelect
                    options={
                      byPackage
                        ? destinationsWithPackages.map((d) => ({
                            value: d.codeIso2Country ?? d.id,
                            label: d.countryName,
                          }))
                        : destinationsInRegions.map((d) => ({
                            value: d.codeIso2Country,
                            label: d.nameCountry,
                          }))
                    }
                    value={dest.destination_Id ?? ""}
                    onChange={(v) => handleDestinationChange(idx, v || null)}
                    placeholder="-- Select Destination --"
                  />
                </div>

                {byPackage && (
                  <div>
                    <label className={styles.formLabel}>Package</label>
                    <SearchableSelect
                      options={dest.packages.map((p) => ({
                        value: p.id,
                        label: p.name || (p as any).packageName || p.id,
                      }))}
                      value={dest.packageId ?? ""}
                      onChange={(v) => handlePackageChange(idx, v || null)}
                      placeholder="-- Select Package --"
                      disabled={!dest.packages.length}
                    />
                  </div>
                )}

                <div>
                  <label className={styles.formLabel}>
                    Nights <span style={{ color: "#ef4444" }}>*</span>
                  </label>
                  <input
                    className={styles.formInput}
                    type="number"
                    min={1}
                    value={dest.numberOfNights}
                    onChange={(e) =>
                      updateDestination(
                        idx,
                        "numberOfNights",
                        Number(e.target.value),
                      )
                    }
                  />
                </div>

                {destinations.length > 1 && (
                  <button
                    type="button"
                    className={`${styles.btnIcon} ${styles.btnIconRemove}`}
                    onClick={() => removeDestination(idx)}
                    title="Remove destination"
                  >
                    <i className="far fa-trash-alt" />
                  </button>
                )}

                {idx === destinations.length - 1 && (
                  <button
                    type="button"
                    className={`${styles.btnIcon} ${styles.btnIconAdd}`}
                    onClick={addDestination}
                    title="Add destination"
                  >
                    <i className="fa-solid fa-plus" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <hr className={styles.divider} />

          {/* ── Notes ── */}
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>Notes</label>
            <textarea
              className={styles.formTextarea}
              placeholder="Any additional notes or requests..."
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          {/* ── Submit ── */}
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={loading || !leadName || !destinationsValid}
          >
            {loading && <span className={styles.spinner} />}
            {loading ? "Submitting..." : "Submit Request"}
            {!loading && <i className="fa-solid fa-paper-plane" />}
          </button>
        </form>
      </div>
    </Wrapper>
  );
}
